import { useLingui } from "@lingui/react/macro";
import type { PaymentConfig } from "@orbin/contracts";
import { useEffect, useState } from "react";
import { PIX_AMOUNTS_CENTS } from "../lib/metering";
import { rpc } from "../lib/rpc";
import { CardCheckout } from "./CardCheckout";
import { PixCheckout } from "./PixCheckout";

/** PIX first; a card is offered only when the deployment takes one. */
export function PaymentCheckout({
  onPaid,
  initialMethod = "pix",
}: {
  onPaid?: () => void;
  initialMethod?: "pix" | "credit_card";
}) {
  const { t } = useLingui();
  const [config, setConfig] = useState<PaymentConfig | null>(null);
  const [method, setMethod] = useState<"pix" | "credit_card">(initialMethod);

  useEffect(() => {
    let active = true;
    void rpc.payments
      .config()
      .then((next) => {
        if (active) setConfig(next);
      })
      .catch(() => undefined);
    return () => {
      active = false;
    };
  }, []);

  const packs = config?.creditPacks.map((pack) => pack.brlCents) ?? [];
  const amounts = packs.length > 0 ? packs : PIX_AMOUNTS_CENTS;
  const cardKey = config?.methods.includes("credit_card") ? config.publicKey : null;
  const pixAvailable = !config || config.methods.includes("pix");
  const active = cardKey && (method === "credit_card" || !pixAvailable) ? "credit_card" : "pix";

  return (
    <div className="flex flex-col gap-4">
      {cardKey && pixAvailable ? (
        <div role="tablist" className="grid grid-cols-2 gap-1 rounded-lg bg-muted p-1 text-sm">
          {(["pix", "credit_card"] as const).map((value) => (
            <button
              key={value}
              type="button"
              role="tab"
              aria-selected={active === value}
              onClick={() => setMethod(value)}
              className={`rounded-md px-3 py-1.5 ${active === value ? "bg-background text-foreground shadow-xs" : "text-muted-foreground"}`}
            >
              {value === "pix" ? "PIX" : t`Card`}
            </button>
          ))}
        </div>
      ) : null}
      {active === "credit_card" && cardKey ? (
        <CardCheckout publicKey={cardKey} amounts={amounts} onPaid={onPaid} />
      ) : (
        <PixCheckout amounts={amounts} onPaid={onPaid ? () => onPaid() : undefined} />
      )}
    </div>
  );
}
